import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VoiceRecorder({ onTranscript }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, `note.${(mime.split("/")[1] || "webm").replace(";codecs=opus", "")}`);
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) onTranscript?.(data.text);
          else toast.error("No transcript returned");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed");
        } finally {
          setBusy(false);
          stream.getTracks().forEach(t => t.stop());
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  useEffect(() => () => { try { mediaRef.current?.stop(); } catch (e) { console.warn("Voice cleanup stop failed", e); } }, []);

  return (
    <button
      type="button"
      data-testid="voice-record-btn"
      onClick={recording ? stop : start}
      disabled={busy}
      className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-sm font-medium border transition-colors
        ${recording ? "bg-red-500 text-white border-red-500 mic-recording" : "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"}`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : recording ? <Square size={14} /> : <Mic size={14} />}
      {busy ? "Transcribing…" : recording ? "Stop" : "Dictate"}
    </button>
  );
}
